/**
 * Phone Number Management Component
 * Handles virtual phone number selection, management, and settings
 */

import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../../auth';
import './PhoneNumberManager.css';

const PhoneNumberManager = ({ user, onPurchaseDone }) => {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [selectedAreaCode, setSelectedAreaCode] = useState('');
  const [numberPricing, setNumberPricing] = useState({});
  const [availableLoading, setAvailableLoading] = useState(false);
  const [areaAvailability, setAreaAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [purchasingNumber, setPurchasingNumber] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({});
  const [aiAssignedNumbers, setAiAssignedNumbers] = useState([]);
  const aiAssignedRef = useRef([]);
  useEffect(() => { aiAssignedRef.current = aiAssignedNumbers; }, [aiAssignedNumbers]);
  const [showPortOutModal, setShowPortOutModal] = useState(false);
  const [portOutInfo, setPortOutInfo] = useState(null);
  const [portOutLoading, setPortOutLoading] = useState(false);
  const [portOutNumber, setPortOutNumber] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const countryCatalog = [
    { code: 'US', name: 'United States', flag: 'US', numberType: 'Local', areaLabel: 'Area code' },
    { code: 'CA', name: 'Canada', flag: 'CA', numberType: 'Local', areaLabel: 'Area code' },
    { code: 'PR', name: 'Puerto Rico', flag: 'PR', numberType: 'Local', areaLabel: 'Area code' },
    { code: 'GB', name: 'United Kingdom', flag: 'GB', numberType: 'Mobile', areaLabel: 'Mobile inventory' },
    { code: 'HK', name: 'Hong Kong', flag: 'HK', numberType: 'Mobile', areaLabel: 'Mobile inventory' }
  ];

  const countries = countryCatalog
    .filter(country => numberPricing[country.code] || Object.keys(numberPricing).length === 0)
    .map(country => ({
      ...country,
      pricing: numberPricing[country.code]?.monthlyDisplay || 'Checking price'
    }));

  const areaCodes = {
    US: [
      { code: '212', city: 'New York, NY' },
      { code: '213', city: 'Los Angeles, CA' },
      { code: '415', city: 'San Francisco, CA' },
      { code: '312', city: 'Chicago, IL' },
      { code: '305', city: 'Miami, FL' },
      { code: '617', city: 'Boston, MA' },
      { code: '206', city: 'Seattle, WA' },
      { code: '404', city: 'Atlanta, GA' },
      { code: '713', city: 'Houston, TX' },
      { code: '214', city: 'Dallas, TX' }
    ],
    CA: [
      { code: '416', city: 'Toronto, ON' },
      { code: '604', city: 'Vancouver, BC' },
      { code: '514', city: 'Montreal, QC' },
      { code: '403', city: 'Calgary, AB' }
    ],
    PR: [
      { code: '787', city: 'San Juan / Metro' },
      { code: '939', city: 'Puerto Rico overlay' }
    ]
  };

  useEffect(() => {
    loadPhoneNumbers();
    loadNumberPricing();
    loadAiConfig();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      loadAvailableNumbers();
    }
  }, [selectedCountry, selectedAreaCode]);

  useEffect(() => {
    if (!showPurchaseModal || !areaCodes[selectedCountry]) return;
    loadAreaAvailability(selectedCountry);
  }, [showPurchaseModal, selectedCountry]);

  const isAreaCodeCountry = (countryCode) => !!areaCodes[countryCode];

  const getCountryMeta = (countryCode) => {
    return countries.find(country => country.code === countryCode) || countryCatalog.find(country => country.code === countryCode) || countryCatalog[0];
  };

  const loadNumberPricing = async () => {
    try {
      const response = await authFetch('/api/users/numfree/pricing');
      if (!response.ok) return;
      const data = await response.json();
      const pricing = {};
      (data.countries || []).forEach(item => {
        pricing[item.countryCode] = item;
      });
      setNumberPricing(pricing);
    } catch (err) {
      console.error('Error loading number pricing:', err);
    }
  };

  const loadPhoneNumbers = async () => {
    try {
      setLoading(true);
      // Load user profile which contains their phone numbers
      const response = await fetch('/api/users/loadprofile', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('nf_token')}` }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const profileData = await response.json();
      
      if (profileData.all && profileData.all.freenumbers) {
        const confirmed = profileData.all.freenumber === profileData.all.freenumberconfirmed;
        const numbers = profileData.all.freenumbers.map(number => {
          const cleaned = number.replace(/\D/g, '');
          // Detect country and area code
          let countryCode = 'International';
          let areaCode = '';
          let city = '';

          if (cleaned.startsWith('1') && cleaned.length === 11) {
            // North American number (+1XXXXXXXXXX)
            areaCode = cleaned.substring(1, 4);
            // Check US area codes first, then CA
            const usMatch = areaCodes.US?.find(a => a.code === areaCode);
            const caMatch = areaCodes.CA?.find(a => a.code === areaCode);
            if (usMatch) {
              countryCode = 'US';
              city = usMatch.city;
            } else if (caMatch) {
              countryCode = 'CA';
              city = caMatch.city;
            } else {
              countryCode = 'US';
              city = `Area ${areaCode}`;
            }
          } else if (cleaned.startsWith('32')) {
            countryCode = 'BE';
            const beCode = cleaned.substring(2, 3);
            const beMatch = areaCodes.BE?.find(a => a.code === beCode);
            city = beMatch ? beMatch.city : 'Belgium';
          } else if (cleaned.startsWith('972')) {
            countryCode = 'IL';
            const ilCode = cleaned.substring(3, 4);
            const ilMatch = areaCodes.IL?.find(a => a.code === ilCode);
            city = ilMatch ? ilMatch.city : 'Israel';
          }

          if (!city) city = countryCode;

          return {
            number: number,
            label: number === profileData.freenum ? 'Primary Number' : 'Additional Number',
            country: countryCode,
            areaCode: areaCode,
            city: city,
            status: profileData.current ? 'active' : 'suspended',
            confirmed: profileData.all.freenumberconfirmed,
            isCurrent: number === profileData.all.freenumber,
            purchaseDate: new Date(profileData.all.beginmonth || Date.now()),
            monthlyPrice: 2.00,
            features: ['SMS', 'Voice', 'Forwarding']
          };
        });
        
        setPhoneNumbers(numbers);
        
        // Load settings from profile data
        const all = profileData.all || {};
        const settingsMap = {};
        numbers.forEach(num => {
          settingsMap[num.number] = {
            label: all.numberLabel || '',
            forwardingEnabled: !!all.activetargetnum1Enabled,
            forwardingNumbers: [all.targetnumber1 || '', all.targetnumber2 || ''].filter(Boolean),
            smsToEmail: !!all.smsToEmailEnabled,
            emailAddress: all.email || '',
            voicemail: !!all.voicemailEnabled
          };
        });
        setSettings(settingsMap);
      } else {
        setPhoneNumbers([]);
      }
    } catch (err) {
      console.error('Error loading phone numbers:', err);
      // Don't show technical JSON parsing errors to users
      if (err.message.includes('Unexpected token') || err.message.includes('JSON')) {
        setError(null); // Silently use mock data when server unavailable
      } else {
        setError('Unable to load phone numbers');
      }
      // Mock data for development
      setPhoneNumbers([
        {
          number: '+1 (555) 987-6543',
          label: 'Main Number',
          country: 'US',
          areaCode: '555',
          city: 'Demo City',
          status: 'active',
          purchaseDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
          monthlyPrice: 2.00,
          features: ['SMS', 'Voice', 'Forwarding']
        },
        {
          number: '+1 (617) 555-0123',
          label: 'Business Line',
          country: 'US',
          areaCode: '617',
          city: 'Boston, MA',
          status: 'active',
          purchaseDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
          monthlyPrice: 2.00,
          features: ['SMS', 'Voice', 'Forwarding']
        }
      ]);
      setSettings({
        '+1 (555) 987-6543': {
          forwardingEnabled: true,
          forwardingNumbers: ['+1 (555) 123-4567'],
          smsToEmail: true,
          emailAddress: 'user@example.com',
          voicemail: true
        },
        '+1 (617) 555-0123': {
          forwardingEnabled: false,
          forwardingNumbers: [],
          smsToEmail: true,
          emailAddress: 'business@example.com',
          voicemail: true
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAiConfig = async () => {
    try {
      var res = await fetch('/api/aiassistant/config', {
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('nf_token') }
      });
      if (res.ok) {
          var cfg = await res.json();
          var nums = (cfg && cfg.assignedNumbers) || (cfg && cfg.config && cfg.config.assignedNumbers) || [];
          if (!nums.length && cfg && cfg.phoneNumber) {
            nums = [cfg.phoneNumber];
          } else if (!nums.length && cfg && cfg.config && cfg.config.phoneNumber) {
            nums = [cfg.config.phoneNumber];
          }
          setAiAssignedNumbers(nums.filter(Boolean));
        }
    } catch(e) {}
  };

  const loadAvailableNumbers = async () => {
    try {
      if (isAreaCodeCountry(selectedCountry) && !selectedAreaCode) {
        setAvailableNumbers([]);
        return;
      }

      setAvailableLoading(true);
      setError(null);
      // Use NumFree's existing Twilio number search API
      const path = selectedAreaCode
        ? `/api/freenumbers/${selectedCountry}/${selectedAreaCode}`
        : `/api/freenumbers/${selectedCountry}`;
      const response = await fetch(path, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('nf_token')}` }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const responseText = await response.text();
      let data = JSON.parse(responseText);
      if (typeof data === 'string') data = JSON.parse(data);
      
      if (data.availablePhoneNumbers) {
        // Extract phone numbers from NumFree API format
        const numbers = data.availablePhoneNumbers.map(num => ({
          phoneNumber: num.phoneNumber,
          locality: num.locality,
          region: num.region,
          numberType: num.numberType,
          addressRequirements: num.addressRequirements
        }));
        setAvailableNumbers(numbers);
      } else {
        setAvailableNumbers([]);
      }
    } catch (err) {
      console.error('Error loading available numbers:', err);
      setAvailableNumbers([]);
      setError('Unable to load available numbers for this area code');
    } finally {
      setAvailableLoading(false);
    }
  };

  const loadAreaAvailability = async (countryCode) => {
    const areas = areaCodes[countryCode] || [];
    if (areas.length === 0) return;

    setAreaAvailability(prev => ({ ...prev, [countryCode]: {} }));

    await Promise.all(areas.map(async (area) => {
      try {
        const response = await fetch(`/api/freenumbers/${countryCode}/${area.code}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('nf_token')}` }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const count = Array.isArray(data.availablePhoneNumbers) ? data.availablePhoneNumbers.length : 0;
        setAreaAvailability(prev => ({
          ...prev,
          [countryCode]: {
            ...(prev[countryCode] || {}),
            [area.code]: count
          }
        }));
      } catch (err) {
        setAreaAvailability(prev => ({
          ...prev,
          [countryCode]: {
            ...(prev[countryCode] || {}),
            [area.code]: 0
          }
        }));
      }
    }));
  };

  const openPurchaseModal = () => {
    setShowPurchaseModal(true);
    if (!isAreaCodeCountry(selectedCountry)) {
      loadAvailableNumbers();
    }
  };

  const loadNumberSettings = async (phoneNumber) => {
    try {
      const response = await fetch(`/api/numbers/${encodeURIComponent(phoneNumber)}/settings`);
      const data = await response.json();
      return data.success ? data.settings : {};
    } catch (err) {
      console.error('Error loading number settings:', err);
      return {};
    }
  };

  const purchaseNumber = async (phoneNumber) => {
    var toStr = function(v) { return typeof v === 'string' ? v : JSON.stringify(v); };
    try {
      setPurchasingNumber(phoneNumber);
      
      // Use NumFree's existing number reservation and purchase flow
      // First reserve the number
      const reserveResponse = await fetch(`/api/users/numfree/request/${encodeURIComponent(phoneNumber)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('nf_token')}` }
      });

      if (!reserveResponse.ok) {
        var reserveErr = await reserveResponse.json().catch(() => ({}));
        if (reserveErr.error === 'Regulatory bundle in review') {
          alert('Your regulatory bundle for ' + (reserveErr.countryCode || 'this country') + ' is still under review by Twilio.\n\n' + (reserveErr.detail || '') + '\n\nYou will be able to purchase this number once the bundle is approved.');
          return;
        }
        if (reserveErr.error === 'Regulatory verification required') {
          var goVerify = window.confirm(
            'A verified address for ' + (reserveErr.countryCode || 'this country') + ' is required.\n\n' +
            (reserveErr.detail || '') + '\n\nGo to Verification page now?'
          );
          if (goVerify) window.location.href = '/verification?country=' + (reserveErr.countryCode || '');
          return;
        }
        throw new Error(toStr(reserveErr.error || reserveErr.detail) || 'Failed to reserve number: HTTP ' + reserveResponse.status);
      }

      const reserveData = await reserveResponse.json();
      if (reserveData.error) throw new Error(toStr(reserveData.error));

      // Then commit the number (this would trigger payment flow in real implementation)
      const commitResponse = await fetch(`/api/users/numfree/commitnb/${encodeURIComponent(phoneNumber)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('nf_token')}` }
      });
      
      const commitData = await commitResponse.json();
      
      if (!commitResponse.ok) {
        if (commitData.error === 'Regulatory bundle in review') {
          alert('Your regulatory bundle for ' + (commitData.countryCode || 'this country') + ' is still under review by Twilio.\n\n' + (commitData.detail || '') + '\n\nYou will be able to purchase once the bundle is approved.');
          return;
        }
        if (commitData.error === 'Regulatory verification required') {
          var goVerify = window.confirm(
            'A verified address for ' + (commitData.countryCode || 'this country') + ' is required.\n\n' +
            (commitData.detail || '') + '\n\nGo to Verification page now?'
          );
          if (goVerify) window.location.href = '/verification?country=' + (commitData.countryCode || '');
          return;
        }
        throw new Error(toStr(commitData.error || commitData.detail) || 'Failed to purchase number: HTTP ' + commitResponse.status);
      }
      
      setShowPurchaseModal(false);
      loadPhoneNumbers(); // Refresh local list
      if (onPurchaseDone) onPurchaseDone(); // Refresh app-level profile
      alert('Phone number purchased successfully!');
    } catch (err) {
      console.error('Error purchasing number:', err);
      alert('Failed to purchase number: ' + err.message);
    } finally {
      setPurchasingNumber(null);
    }
  };

  const updateNumberSettings = async (phoneNumber, newSettings) => {
    try {
      const updatePromises = [];

      // Save forwarding number 1
      if (newSettings.forwardingNumbers && newSettings.forwardingNumbers[0]) {
        updatePromises.push(
          authFetch(`/api/users/numfree/targetnb1/${encodeURIComponent(newSettings.forwardingNumbers[0])}`, { method: 'POST' })
        );
      }

      // Save forwarding number 2
      if (newSettings.forwardingNumbers && newSettings.forwardingNumbers[1]) {
        updatePromises.push(
          authFetch(`/api/users/numfree/targetnb2/${encodeURIComponent(newSettings.forwardingNumbers[1])}`, { method: 'POST' })
        );
      }

      // Toggle forwarding enabled/disabled
      updatePromises.push(
        authFetch(`/api/users/numfree/setactivetargetnb/1/${newSettings.forwardingEnabled ? 'true' : 'false'}`, { method: 'POST' })
      );

      // Wait for all
      const responses = await Promise.all(updatePromises);
      const failed = responses.filter(r => !r.ok);
      if (failed.length > 0) {
        throw new Error(`Failed to update settings: ${failed[0].status}`);
      }

      // Update local state
      setSettings(prev => ({
        ...prev,
        [phoneNumber]: { ...prev[phoneNumber], ...newSettings }
      }));

      alert('Settings updated successfully!');
    } catch (err) {
      console.error('Error updating settings:', err);
      alert('Failed to update settings: ' + err.message);
    }
  };

  const releaseNumber = async (phoneNumber) => {
    if (!window.confirm(`Are you sure you want to release ${phoneNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/numbers/${encodeURIComponent(phoneNumber)}/release`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.success) {
        loadPhoneNumbers(); // Refresh the list
        alert('Phone number released successfully');
      } else {
        throw new Error(data.error || 'Failed to release number');
      }
    } catch (err) {
      console.error('Error releasing number:', err);
      alert('Failed to release number: ' + err.message);
    }
  };

  const formatPhoneNumber = (number) => {
    // Format phone number for display
    const cleaned = number.replace(/\D/g, '');
    if (cleaned.startsWith('1') && cleaned.length === 11) {
      const formatted = cleaned.substring(1);
      return `+1 (${formatted.substring(0, 3)}) ${formatted.substring(3, 6)}-${formatted.substring(6)}`;
    }
    return number;
  };

  const openSettings = (number) => {
    setSelectedNumber(number);
    setShowSettingsModal(true);
  };

  const openPortOut = async (phone) => {
    setPortOutNumber(phone);
    setShowPortOutModal(true);
    setPortOutInfo(null);
    setPortOutLoading(true);
    setCopiedField(null);

    try {
      const response = await fetch(`/api/porting/port-out-info/${encodeURIComponent(phone.number)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('nf_token')}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setPortOutInfo(data);
      } else {
        throw new Error(data.error || 'Failed to load port-out info');
      }
    } catch (err) {
      console.error('Error loading port-out info:', err);
      setError('Unable to load port-out information');
      setShowPortOutModal(false);
    } finally {
      setPortOutLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const copyAllPortOutInfo = () => {
    if (!portOutInfo) return;
    const text = [
      `Account Number: ${portOutInfo.accountNumber}`,
      `PIN: ${portOutInfo.pin}`,
      `Carrier: ${portOutInfo.carrierName}`,
      `Account Holder: ${portOutInfo.accountHolderName}`,
      `Phone Number: ${formatPhoneNumber(portOutInfo.phoneNumber)}`
    ].join('\n');
    copyToClipboard(text, 'all');
  };

  if (loading) {
    return (
      <div className="phone-manager">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading phone numbers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="phone-manager">
      <div className="manager-header">
        <h2>Phone Number Management</h2>
        <button 
          className="btn btn-primary" 
          onClick={openPurchaseModal}
        >
          <i className="fas fa-plus"></i> Add Number
        </button>
      </div>

      {/* Current Numbers */}
      <div className="current-numbers">
        <h3>Your Numbers ({phoneNumbers.length})</h3>
        
        {phoneNumbers.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-phone fa-3x"></i>
            <h4>No Phone Numbers</h4>
            <p>Purchase your first virtual phone number to get started</p>
            <button 
              className="btn btn-primary" 
              onClick={openPurchaseModal}
            >
              Get Your First Number
            </button>
          </div>
        ) : (
          <div className="numbers-grid">
            {phoneNumbers.map(phone => (
              <div key={phone.number} className="number-card">
                <div className="number-header">
                  <div className="number-info">
                    <h4>{formatPhoneNumber(phone.number)}</h4>
                    <span className="number-label">{phone.label}</span>
                  </div>
                  <div className="number-status">
                    <span className={`status ${phone.confirmed ? 'active' : phone.isCurrent ? 'not-purchased' : phone.status}`}>
                      {phone.confirmed ? '✅ Active' : phone.isCurrent ? 'Not Purchased' : phone.status === 'active' ? '✅ Active' : '⏸️ Suspended'}
                    </span>
                  </div>
                </div>

                <div className="number-details">
                  <div className="detail-item">
                    <span className="label">Location:</span>
                    <span>{phone.city}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Monthly:</span>
                    <span>${phone.monthlyPrice}/month</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Features:</span>
                    <div className="features">
                      {phone.features.map(feature => (
                        <span key={feature} className="feature-tag">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Quick Settings Display */}
                {settings[phone.number] && (
                  <div className="quick-settings">
                    {settings[phone.number].forwardingEnabled && (
                      <span className="setting-indicator">
                        <i className="fas fa-phone-forward"></i> Forwarding
                      </span>
                    )}
                    {settings[phone.number].smsToEmail && (
                      <span className="setting-indicator">
                        <i className="fas fa-envelope"></i> SMS→Email
                      </span>
                    )}
                    {settings[phone.number].voicemail && (
                      <span className="setting-indicator">
                        <i className="fas fa-voicemail"></i> Voicemail
                      </span>
                    )}
                    {aiAssignedNumbers.indexOf(phone.number) >= 0 && (
                      <span className="setting-indicator ai-indicator">
                        <i className="fas fa-robot"></i> AI
                      </span>
                    )}
                  </div>
                )}

                <div className="number-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => openSettings(phone)}
                  >
                    <i className="fas fa-cog"></i> Settings
                  </button>
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={() => openPortOut(phone)}
                  >
                    <i className="fas fa-sign-out-alt"></i> Port Out
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => releaseNumber(phone.number)}
                  >
                    <i className="fas fa-trash"></i> Release
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <div className="modal-overlay" onClick={() => setShowPurchaseModal(false)}>
          <div className="modal purchase-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Purchase Phone Number</h3>
              <button 
                className="close-btn"
                onClick={() => setShowPurchaseModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-content purchase-flow">
              <div className="picker-hero">
                <div>
                  <span className="eyebrow">New number</span>
                  <h4>Choose a country and area code</h4>
                </div>
                <div className="price-pill">
                  <span>{getCountryMeta(selectedCountry).pricing}</span>
                  <small>{getCountryMeta(selectedCountry).numberType}</small>
                </div>
              </div>

              <div className="country-select">
                <label>Country</label>
                <select
                  value={selectedCountry}
                  onChange={e => {
                    setSelectedCountry(e.target.value);
                    setSelectedAreaCode('');
                    setAvailableNumbers([]);
                  }}
                >
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name} · {country.numberType} · {country.pricing}
                    </option>
                  ))}
                </select>
              </div>

              {isAreaCodeCountry(selectedCountry) ? (
                <div className="area-picker">
                  <div className="picker-section-header">
                    <div>
                      <h4>{getCountryMeta(selectedCountry).areaLabel}</h4>
                      <p>Unavailable areas are hidden after the inventory check completes.</p>
                    </div>
                  </div>
                  <div className="area-chip-grid">
                    {(areaCodes[selectedCountry] || []).map(area => {
                      const count = areaAvailability[selectedCountry]?.[area.code];
                      if (count === 0) return null;
                      return (
                        <button
                          type="button"
                          key={area.code}
                          className={`area-chip ${selectedAreaCode === area.code ? 'selected' : ''}`}
                          onClick={() => setSelectedAreaCode(area.code)}
                          disabled={count === undefined}
                        >
                          <strong>{area.code}</strong>
                          <span>{area.city}</span>
                          <small>{count === undefined ? 'Checking' : `${count}+ available`}</small>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div className="available-numbers">
                <div className="picker-section-header">
                  <div>
                    <h4>Available Numbers</h4>
                    <p>{availableLoading ? 'Checking number availability...' : `${availableNumbers.length} matching numbers`}</p>
                  </div>
                </div>
                <div className="numbers-list modern">
                  {availableLoading ? (
                    <div className="inventory-empty">Checking inventory...</div>
                  ) : availableNumbers.length === 0 ? (
                    <div className="inventory-empty">
                      {isAreaCodeCountry(selectedCountry) && !selectedAreaCode
                        ? 'Choose an available area code to see numbers.'
                        : 'No SMS-out capable numbers available for this selection.'}
                    </div>
                  ) : availableNumbers.map(item => (
                    <div key={item.phoneNumber} className="available-number">
                      <span className="number-block">
                        <strong>{formatPhoneNumber(item.phoneNumber)}</strong>
                        <small>{[item.locality, item.region, item.numberType].filter(Boolean).join(' / ') || getCountryMeta(selectedCountry).numberType}</small>
                      </span>
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => purchaseNumber(item.phoneNumber)}
                        disabled={purchasingNumber !== null}
                      >
                        {purchasingNumber === item.phoneNumber ? 'Purchasing...' : 'Purchase'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && selectedNumber && (
        <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Settings for {formatPhoneNumber(selectedNumber.number)}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowSettingsModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <NumberSettingsForm 
                phoneNumber={selectedNumber.number}
                settings={settings[selectedNumber.number] || {}}
                onUpdate={updateNumberSettings}
                onClose={() => setShowSettingsModal(false)}
                aiAssigned={aiAssignedNumbers.indexOf(selectedNumber.number) >= 0}
                onToggleAi={async (num, assign) => {
                  var current = aiAssignedRef.current;
                  var next = assign
                    ? current.concat(num).filter((v, i, a) => a.indexOf(v) === i)
                    : current.filter(n => n !== num);
                  setAiAssignedNumbers(next);
                  try {
                    var res = await fetch('/api/aiassistant/config', {
                      method: 'PUT',
                      headers: {
                        'Authorization': 'Bearer ' + localStorage.getItem('nf_token'),
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({ assignedNumbers: next })
                    });
                    if (!res.ok) {
                      var err = await res.json().catch(() => ({}));
                      console.error('[AI Toggle] Save failed:', res.status, err);
                      setAiAssignedNumbers(current);
                      window.alert('Failed to save AI assignment: ' + (err.error || ('HTTP ' + res.status)));
                    }
                  } catch (e) {
                    console.error('[AI Toggle] Network error:', e);
                    setAiAssignedNumbers(current);
                    window.alert('Network error saving AI assignment');
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Port Out Info Modal */}
      {showPortOutModal && (
        <div className="modal-overlay" onClick={() => setShowPortOutModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Port Out Information{portOutNumber ? ` for ${formatPhoneNumber(portOutNumber.number)}` : ''}</h3>
              <button
                className="close-btn"
                onClick={() => setShowPortOutModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              {portOutLoading ? (
                <div className="loading">
                  <div className="loading-spinner"></div>
                  <p>Loading port-out information...</p>
                </div>
              ) : portOutInfo ? (
                <div className="port-out-info">
                  <p className="port-out-instructions">
                    Provide this information to your new carrier to transfer your number. Keep your NumFree service active until the port completes.
                  </p>

                  <div className="port-out-fields">
                    <div className="port-out-field">
                      <label>Account Number</label>
                      <div className="field-value">
                        <code>{portOutInfo.accountNumber}</code>
                        <button
                          className="btn-copy"
                          onClick={() => copyToClipboard(portOutInfo.accountNumber, 'accountNumber')}
                          title="Copy"
                        >
                          {copiedField === 'accountNumber' ? '✓' : '📋'}
                        </button>
                      </div>
                    </div>

                    <div className="port-out-field">
                      <label>PIN</label>
                      <div className="field-value">
                        <code>{portOutInfo.pin}</code>
                        <button
                          className="btn-copy"
                          onClick={() => copyToClipboard(portOutInfo.pin, 'pin')}
                          title="Copy"
                        >
                          {copiedField === 'pin' ? '✓' : '📋'}
                        </button>
                      </div>
                    </div>

                    <div className="port-out-field">
                      <label>Carrier</label>
                      <div className="field-value">
                        <code>{portOutInfo.carrierName}</code>
                        <button
                          className="btn-copy"
                          onClick={() => copyToClipboard(portOutInfo.carrierName, 'carrier')}
                          title="Copy"
                        >
                          {copiedField === 'carrier' ? '✓' : '📋'}
                        </button>
                      </div>
                    </div>

                    <div className="port-out-field">
                      <label>Account Holder</label>
                      <div className="field-value">
                        <code>{portOutInfo.accountHolderName}</code>
                        <button
                          className="btn-copy"
                          onClick={() => copyToClipboard(portOutInfo.accountHolderName, 'holder')}
                          title="Copy"
                        >
                          {copiedField === 'holder' ? '✓' : '📋'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="port-out-actions">
                    <button className="btn btn-primary" onClick={copyAllPortOutInfo}>
                      {copiedField === 'all' ? '✓ Copied!' : '📋 Copy All'}
                    </button>
                    <button className="btn btn-secondary" onClick={() => setShowPortOutModal(false)}>
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <p>Unable to load port-out information.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="error-toast">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}
    </div>
  );
};

// Normalize phone for comparison
const normPhone = (n) => {
  if (!n) return '';
  var c = n.replace(/[\s\-\(\)\.]/g, '');
  if (/^\d{10}$/.test(c)) return '+1' + c;
  if (/^1\d{10}$/.test(c)) return '+' + c;
  return c.startsWith('+') ? c : '+' + c;
};

// Number Settings Form Component
const NumberSettingsForm = ({ phoneNumber, settings, onUpdate, onClose, aiAssigned, onToggleAi }) => {
  const [formData, setFormData] = useState({
    label: settings.label || '',
    forwardingEnabled: settings.forwardingEnabled || false,
    forwardingNumbers: settings.forwardingNumbers || [''],
    ...settings
  });
  // Track which numbers are verified (from DB) and which are being verified now
  const [verifiedNumbers] = useState(() => {
    const v = {};
    // Numbers already saved + verified in the DB don't need re-verification
    (settings.forwardingNumbers || []).forEach(n => { if (n) v[normPhone(n)] = true; });
    return v;
  });
  const [verifyState, setVerifyState] = useState({}); // { [normalized]: 'sending' | 'sent' | 'verifying' | 'verified' | 'error' }
  const [verifyCode, setVerifyCode] = useState({});
  const [verifyError, setVerifyError] = useState({});

  const isVerified = (num) => {
    const n = normPhone(num);
    return !n || verifiedNumbers[n] || verifyState[n] === 'verified';
  };

  const needsVerification = (num) => {
    const n = normPhone(num);
    return n && n.length >= 8 && !isVerified(num);
  };

  const sendVerifyCode = async (num, index) => {
    const n = normPhone(num);
    setVerifyState(prev => ({ ...prev, [n]: 'sending' }));
    setVerifyError(prev => ({ ...prev, [n]: null }));
    try {
      const res = await authFetch('/api/users/numfree/verify-target/send', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ number: n })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setVerifyState(prev => ({ ...prev, [n]: 'sent' }));
      } else {
        setVerifyState(prev => ({ ...prev, [n]: 'error' }));
        setVerifyError(prev => ({ ...prev, [n]: data.error || 'Failed to send code' }));
      }
    } catch (e) {
      setVerifyState(prev => ({ ...prev, [n]: 'error' }));
      setVerifyError(prev => ({ ...prev, [n]: e.message }));
    }
  };

  const confirmCode = async (num, index) => {
    const n = normPhone(num);
    const code = (verifyCode[n] || '').trim();
    if (!code) return;
    setVerifyState(prev => ({ ...prev, [n]: 'verifying' }));
    try {
      const res = await authFetch('/api/users/numfree/verify-target/confirm', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ number: n, code, targetOrder: String(index + 1) })
      });
      const data = await res.json();
      if (res.ok && data.verified) {
        setVerifyState(prev => ({ ...prev, [n]: 'verified' }));
        verifiedNumbers[n] = true;
      } else {
        setVerifyState(prev => ({ ...prev, [n]: 'sent' }));
        setVerifyError(prev => ({ ...prev, [n]: data.error || 'Wrong code' }));
      }
    } catch (e) {
      setVerifyState(prev => ({ ...prev, [n]: 'sent' }));
      setVerifyError(prev => ({ ...prev, [n]: e.message }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Check all forwarding numbers are verified
    const unverified = (formData.forwardingNumbers || []).filter(n => n && needsVerification(n));
    if (unverified.length > 0) {
      alert('Please verify all forwarding numbers before saving.');
      return;
    }
    onUpdate(phoneNumber, formData);
    onClose();
  };

  const updateForwardingNumber = (index, value) => {
    setFormData(prev => ({
      ...prev,
      forwardingNumbers: prev.forwardingNumbers.map((num, i) => i === index ? value : num)
    }));
  };

  const removeForwardingNumber = (index) => {
    setFormData(prev => ({
      ...prev,
      forwardingNumbers: prev.forwardingNumbers.filter((_, i) => i !== index)
    }));
  };

  const addForwardingNumber = () => {
    setFormData(prev => ({
      ...prev,
      forwardingNumbers: [...prev.forwardingNumbers, '']
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="settings-form">
      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={formData.forwardingEnabled}
            onChange={(e) => setFormData(prev => ({ ...prev, forwardingEnabled: e.target.checked }))}
          />
          <span>Enable Call Forwarding</span>
        </label>
      </div>

      {formData.forwardingEnabled && (
        <div className="form-group">
          <label>Forwarding Numbers:</label>
          {formData.forwardingNumbers.map((number, index) => {
            const n = normPhone(number);
            const state = verifyState[n];
            const verified = isVerified(number);
            const err = verifyError[n];
            return (
              <div key={index} className="forwarding-number-row">
                <div className="forwarding-number-input">
                  <input
                    type="tel"
                    value={number}
                    onChange={(e) => updateForwardingNumber(index, e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    disabled={verified}
                  />
                  {verified ? (
                    <span className="verify-badge verified">Verified</span>
                  ) : n && n.length >= 8 ? (
                    <button type="button" className="btn btn-sm btn-verify"
                      disabled={state === 'sending'}
                      onClick={() => sendVerifyCode(number, index)}>
                      {state === 'sending' ? 'Sending...' : state === 'sent' || state === 'error' ? 'Resend' : 'Verify'}
                    </button>
                  ) : null}
                  <button type="button" className="btn btn-danger btn-sm"
                    onClick={() => removeForwardingNumber(index)}>
                    Remove
                  </button>
                </div>
                {(state === 'sent' || state === 'verifying') && (
                  <div className="verify-code-row">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="Enter 6-digit code"
                      value={verifyCode[n] || ''}
                      onChange={(e) => setVerifyCode(prev => ({ ...prev, [n]: e.target.value.replace(/\D/g, '') }))}
                      className="verify-code-input"
                    />
                    <button type="button" className="btn btn-sm btn-primary"
                      disabled={state === 'verifying' || (verifyCode[n] || '').length < 4}
                      onClick={() => confirmCode(number, index)}>
                      {state === 'verifying' ? 'Checking...' : 'Confirm'}
                    </button>
                  </div>
                )}
                {err && <div className="verify-error">{err}</div>}
              </div>
            );
          })}
          <button type="button" className="btn btn-secondary btn-sm" onClick={addForwardingNumber}>
            + Add Number
          </button>
        </div>
      )}

      <div className="form-group">
        <label className="checkbox-label disabled">
          <input type="checkbox" checked={true} disabled />
          Forward SMS to Email <em style={{fontSize:'11px',color:'#999',marginLeft:4}}>(always on)</em>
        </label>
      </div>

      <div className="form-group">
        <label className="checkbox-label disabled">
          <input type="checkbox" checked={true} disabled />
          Voicemail <em style={{fontSize:'11px',color:'#999',marginLeft:4}}>(always on)</em>
        </label>
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={aiAssigned || false}
            onChange={(e) => onToggleAi(phoneNumber, e.target.checked)}
          />
          AI Call Handling <em style={{fontSize:'11px',color:'#666',marginLeft:4}}>(your AI assistant answers this number)</em>
        </label>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary">Save Settings</button>
      </div>
    </form>
  );
};

export default PhoneNumberManager;
